import { useTranslation } from 'next-i18next';
import { useEffect, useState } from 'react';
import { CTAButtonComponentInterface } from '../../../../../interfaces/pagebuilder';
import { useSelector } from '../../../../../reduxToolkit/typedRedux';
import VyNoData from '../../../../common/VyNoData';
import { PageBuilderSettingsSection, PageBuilderSettingsTable } from '../PageBuilderSettingsTable';
import CTAPageSelect from './CTAPageSelect';
import VyTooltip from '../../../../common/VyTooltip';
import { MdAdd, MdDelete, MdEdit } from 'react-icons/md';
import { Alert, IconButton, MenuItem } from '@mui/material';
import { DataUpdateModal } from './DataUpdateModal';
import { FieldValuePair } from '../../../../../interfaces/interfaces';

interface CTAButtonSettingsTableInterface {
  component: CTAButtonComponentInterface;
  onChange: (c: CTAButtonComponentInterface) => void;
}

const CTAButtonSettingsTable = ({ component, onChange }: CTAButtonSettingsTableInterface) => {
  const { t } = useTranslation(['pagebuilder']);

  const pages = useSelector((state) => state.common.pages).filter((p) => p.visibility?.visibleWeb === true);
  const event = useSelector((state) => state.common.event);

  const [profileFieldModal, setProfileFieldModal] = useState<FieldValuePair | undefined>();

  const guestmanagementFields = useSelector((state) => state.common.registrationFields);
  const selectFields = guestmanagementFields.filter(
    (field) => (field.fieldType === 'text' && field.textValueOptions.length > 0) || field.fieldType === 'language'
  );

  const [showPages, setShowPages] = useState<boolean>(false);

  function isVystemUrl(url?: string | null) {
    if (!url) return false;
    try {
      const formattedUrl = new URL(url);
      return location.host === formattedUrl.host;
    } catch (error) {
      console.error(error);
      return false;
    }
  }

  useEffect(() => {
    setShowPages(
      (!component.isExternal && component.buttonType !== 'data-update') ||
        (component.buttonType === 'data-update' && component.dataUpdateAction === 'open_page')
    );
  }, [component]);

  const selectedUpdateFieldExists =
    !!component.dataUpdateFields &&
    selectFields &&
    selectFields.length > 0 &&
    !!selectFields.find((f) => component.dataUpdateFields.find((d) => d.field === f._id));

  const settingSections: PageBuilderSettingsSection[] = [
    {
      settings: [
        {
          label: t('pagebuilder:title'),
          path: 'title',
          type: 'text',
        },
        {
          label: t('pagebuilder:cta.button-type.title'),
          path: 'buttonType',
          type: 'select',
          defaultValue: component.buttonType ?? 'link',
          selectItems: [
            { name: t('pagebuilder:cta.button-type.link'), value: 'link' },
            { name: t('pagebuilder:cta.button-type.data-update'), value: 'data-update' },
          ],
        },
        {
          label: t('pagebuilder:link'),
          path: 'url',
          type: 'text',
          visible: component.isExternal && component.buttonType !== 'data-update',
          content: isVystemUrl(component.url) ? (
            <Alert severity="warning" className="mb-2">
              {t('pagebuilder:is-vystem-url', { setting: t('pagebuilder:is-external') })}
            </Alert>
          ) : undefined,
        },
        {
          tooltip: t('pagebuilder:tooltip-is-external'),
          label: t('pagebuilder:is-external'),
          path: 'isExternal',
          type: 'boolean',
          visible: component.buttonType !== 'data-update',
        },
        {
          tooltip: t('pagebuilder:tooltip-new-tab'),
          label: t('pagebuilder:new-tab'),
          path: 'newTab',
          type: 'boolean',
          visible: component.buttonType !== 'data-update',
        },
        {
          renderSettingComponent: () => <VyNoData noDataText={t('pagebuilder:cta.no-fields-for-data-update')} />,
          visible: component.buttonType === 'data-update' && selectFields.length === 0,
        },
      ],
    },
    {
      settings: [
        {
          label: t('pagebuilder:cta.data-update-action'),
          path: 'dataUpdateAction',
          type: 'select',
          defaultValue: selectedUpdateFieldExists ? component.dataUpdateAction : 'nothing',
          selectItems: [
            { name: t('pagebuilder:none'), value: 'nothing' },
            { name: t('pagebuilder:visit-page'), value: 'open_page' },
            { name: t('pagebuilder:visit-url'), value: 'open_url' },
          ],
          visible: component.buttonType === 'data-update' && selectedUpdateFieldExists,
        },
        {
          tooltip: t('pagebuilder:tooltip-new-tab'),
          label: t('pagebuilder:new-tab'),
          path: 'newTab',
          type: 'boolean',
          visible:
            component.buttonType === 'data-update' && selectedUpdateFieldExists && !!component.dataUpdateAction && component.dataUpdateAction !== 'nothing',
        },
        {
          label: t('pagebuilder:link'),
          path: 'url',
          type: 'text',
          visible: component.buttonType === 'data-update' && component.dataUpdateAction === 'open_url',
          content: isVystemUrl(component.url) ? (
            <Alert severity="warning" className="mb-2">
              {t('pagebuilder:is-vystem-url-post-action', { action: t('pagebuilder:visit-page') })}
            </Alert>
          ) : undefined,
        },
        {
          label: t('common:languages.english'),
          content: <CTAPageSelect component={component} languageKey="en" pages={pages} onChange={onChange} />,
          visible: showPages && event?.languages && event?.languages?.includes('en'),
        },
        {
          label: t('common:languages.german'),
          content: <CTAPageSelect component={component} languageKey="de" pages={pages} onChange={onChange} />,
          visible: showPages && event?.languages && event?.languages?.includes('de'),
        },
        {
          label: t('common:languages.french'),
          content: <CTAPageSelect component={component} languageKey="fr" pages={pages} onChange={onChange} />,
          visible: showPages && event?.languages && event?.languages?.includes('fr'),
        },
      ],
    },
    {
      name: t('account:profile-field-updates'),
      visible: component.buttonType === 'data-update',
      actions: [
        {
          disabled: selectFields.length === component.dataUpdateFields.length,
          icon: (
            <VyTooltip title={t('account:add')}>
              <MdAdd />
            </VyTooltip>
          ),
          onClick: () => {
            setProfileFieldModal({});
          },
        },
      ],
      settings: [
        {
          content: (
            <>
              {component.dataUpdateFields.map((field) => {
                return (
                  <div key={field.field} className="flex items-center justify-between w-full ">
                    <p>{selectFields.find((f) => f._id === field?.field)?.label}</p>
                    <div className="flex">
                      <IconButton onClick={() => setProfileFieldModal(field)}>
                        <MdEdit />
                      </IconButton>
                      <IconButton
                        onClick={() => {
                          onChange({
                            ...component,
                            dataUpdateFields: component.dataUpdateFields.filter((f) => f.field !== field.field),
                          });
                        }}>
                        <MdDelete />
                      </IconButton>
                    </div>
                  </div>
                );
              })}
            </>
          ),
          onChange: () => {},
        },
      ],
    },
  ];

  return (
    <>
      {!!profileFieldModal && (
        <DataUpdateModal onChange={onChange} onClose={() => setProfileFieldModal(undefined)} component={component} profileField={profileFieldModal} />
      )}
      <PageBuilderSettingsTable sections={settingSections} onChange={onChange} component={component} showTitleAndDescription={false} />
    </>
  );
};
export default CTAButtonSettingsTable;
